import { useEffect, useState } from "react"
import type { DTADocumentContent, DTAVersion } from "../types/dta"
import { dtaApiService } from "../services/dtaApi"
import { computeInlineDiff, type InlineDiffResult } from "../utils/inlineDiff"

/**
 * Computes inline diff badges by comparing the current version's document_content against the
 * previous version. Returns null if there's no previous version.
 */
export function useInlineDiff(
  dtaId: string | undefined,
  currentVersion: string,
  versions: DTAVersion[],
  currentContent: DTADocumentContent | undefined,
): InlineDiffResult | null {
  const [diff, setDiff] = useState<InlineDiffResult | null>(null)

  useEffect(() => {
    if (!dtaId || !currentVersion || versions.length < 2 || !currentContent) {
      setDiff(null)
      return
    }

    const idx = versions.findIndex((v) => v.version === currentVersion)
    if (idx < 0 || idx >= versions.length - 1) {
      setDiff(null)
      return
    }

    const prevVersion = versions[idx + 1].version
    void dtaApiService
      .getVersion(dtaId, prevVersion)
      .then((prev) => {
        const prevContent = prev.document_content
        setDiff(computeInlineDiff(prevContent, currentContent))
      })
      .catch(() => setDiff(null))
  }, [dtaId, currentVersion, versions, currentContent])

  return diff
}
